import { useState, useEffect, useCallback, useTransition, useMemo } from 'react';
import { debounce } from 'lodash';
import { ContentContainer } from '../../../components/Filters/components/ContentContainer';
import { ControlsPanel } from '../../../components/Filters/components/ControlsPanel';
import { CheckboxList } from '../../../components/Filters/components/CheckboxList';
import { SearchInput } from '../../../components/Filters/components/SearchInput';
import { ItemBadgesContainer } from '../../../components/Filters/components/ItemBadgesContainer';
import { FilterLoader } from '../../../components/Filters/components/FilterLoader';
import { NoResults } from '../../../components/Filters/components/NoResults';
import { API } from '../../../API';
import { showRequestError } from '../../../utils/requestError';
import { useTranslation } from 'react-i18next';
import { useSearchContext } from '../contexts';
export interface FeedOption {
  value: number;
  label: string;
}

export interface FeedsResponse {
  objects: FeedData[];
  total_count: number;
}

export interface FeedData {
  id: number;
  name: string;
}

export const FeedsChooser = () => {
  const { t } = useTranslation();
  const { state, updateDraftFilters } = useSearchContext();
  const [feeds, setFeeds] = useState<FeedOption[]>([]);
  const [selectedFeeds, setSelectedFeeds] = useState<FeedOption[]>([]);
  const [defaultOptions, setDefaultOptions] = useState<FeedOption[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isPending, startTransition] = useTransition();

  const page = 1;
  const pageSize = 30;

  const selected = state.draftFilters.feeds.value;
  
  const getSelectedFeeds = (selected: number[]) => {
    if (selected.length === 0) {
      return Promise.resolve([]);
    }

    const urlParams = new URLSearchParams();

    selected.forEach((id: number) => {
      urlParams.append('ids', id.toString());
    });

    return API.fetch('GET', `/API/v1/feeds?${urlParams.toString()}`)
      .then(data => {
        let selected = data.objects
          .map((feed: FeedData) => {
            return {
              value: feed.id,
              label: feed.name,
            };
          })
          return selected;
      })
      .catch((error) => {
        showRequestError(error, t('Feeds'));
      });
  }

  useEffect(() => {
    let isCancelled = false;

    const fetchSelected = async () => {
      const result = await getSelectedFeeds(state.draftFilters.feeds.value);
      if (!isCancelled) {
        setSelectedFeeds(result || []);
      }
    };

    fetchSelected();

    return () => {
      isCancelled = true;
    };
  }, [state.draftFilters.feeds.value]);

  const getAllFeeds = useCallback(() => {
      startTransition(() => {
        API.fetch('GET', `/API/v1/feeds?page=${page}&page_size=${pageSize}`)
          .then((data: FeedsResponse) => {
            let optionsList = data.objects
              .slice(0, 10)
              .map((feed: FeedData) => {
                return {value: feed.id, label: feed.name};
              })
              setDefaultOptions(optionsList)
          })
          .catch((error) => {
            showRequestError(error, t('Feeds'));
          });
      });
    }, []);
  
    useEffect(getAllFeeds, [getAllFeeds]);

    const getFeeds = (inputValue: string) => {
      if (!inputValue) {
        setFeeds([]);
        return;
      }

      startTransition(() => {
        API.fetch('GET', `/API/v1/feeds?q=${inputValue}&page=${page}&page_size=${pageSize}`)
          .then((data: FeedsResponse) => {
            let optionsList = data.objects
              .map((feed: FeedData) => {
                return {value: feed.id, label: feed.name};
              })
            setFeeds(optionsList);
          })
          .catch((error) => {
            showRequestError(error, t('Feeds'));
          });
      });
    };

    const debouncedSearch = useCallback(
      debounce((value: string) => {
        getFeeds(value);
      }, 500),
      []
    );

    useEffect(() => {
      return () => {
        debouncedSearch.cancel();
      };
    }, [debouncedSearch]);

    const handleSearchChange = (value: string) => {
      setSearchValue(value);
      debouncedSearch(value);
    };

  const handleClearAll = () => {
    updateDraftFilters({
      feeds: {
        value: [],
        exclude: false
      }
    });
  };

  const handleToggleFeed = useCallback((feedId: number) => {
    const selected = state.draftFilters.feeds.value;

    if (selected.includes(feedId)) {
      updateDraftFilters({
        feeds: {
          value: selected.filter(id => id !== feedId),
          exclude: state.draftFilters.feeds.exclude
        }
      });
    } else {
      updateDraftFilters({
        feeds: {
          value: [...selected, feedId],
          exclude: state.draftFilters.feeds.exclude
        }
      });
    }
  }, [state.draftFilters.feeds.value, state.draftFilters.feeds.exclude, updateDraftFilters]);

  const itemsToShow = searchValue ? feeds : defaultOptions;

  // Memoize the checkbox items to prevent recreating on every render
  const checkboxItems = useMemo(() => {
    return itemsToShow.map((feed: FeedOption) => ({
      name: feed.label,
      checked: selected.includes(feed.value),
      onChange: () => handleToggleFeed(feed.value),
    }));
  }, [itemsToShow, selected, handleToggleFeed]);

  // Memoize badges to prevent recreating on every render
  const badges = useMemo(() => {
    return selectedFeeds.map((feed: FeedOption) => ({
      name: feed.label,
      onChange: () => handleToggleFeed(feed.value),
    }));
  }, [selectedFeeds, handleToggleFeed]);

  return (
    <ContentContainer
      actionsComponent={
        <ControlsPanel
          id="feeds-filter"
          count={state.draftFilters.feeds.value.length}
          isExclude={state.draftFilters.feeds.exclude}
          showExcludeSwitch={true}
          showSelectAll={false}
          onClearAll={handleClearAll}
          onSelectAll={() => {}}
          onExcludeClick={() => {
            updateDraftFilters({
              feeds: {
                value: state.draftFilters.feeds.value,
                exclude: !state.draftFilters.feeds.exclude
              }
            });
          }}
        />
      }
      searchComponent={
        <SearchInput
          value={searchValue}
          onChange={handleSearchChange}
          placeholder={t('Search feeds')}
        />
      }

      badgesComponent={
        selectedFeeds.length > 0 ? (
          <ItemBadgesContainer badges={badges} />
        ) : undefined
      }
    >
      {isPending && <FilterLoader />}
      {!isPending && itemsToShow.length > 0 && (
        <CheckboxList items={checkboxItems} />
      )}
      {!isPending && searchValue && itemsToShow.length === 0 && <NoResults />}
    </ContentContainer>
  );
};